import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, GripVertical } from "lucide-react";
import { ApiService } from "../../../services/api";
import { useModal } from "../../../hooks/useModal";
import QuestionManager from "./QuestionManager";

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
}

interface SectionManagerProps {
  surveyId: string;
  canUpdateSurvey: boolean;
  onSectionsChange?: (sections: Section[]) => void;
}

export default function SectionManager({ surveyId, canUpdateSurvey, onSectionsChange }: SectionManagerProps) {
  const { showAlert, showConfirm } = useModal();
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [deletingSection, setDeletingSection] = useState<string | null>(null);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const [sectionForm, setSectionForm] = useState({
    title: "",
    description: "",
  });

  const loadSections = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getSurveySections(surveyId);
      const sectionsList = Array.isArray(response) ? response : response?.data || [];
      const sortedSections = sectionsList.sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
      setSections(sortedSections);
      onSectionsChange?.(sortedSections);

      if (expandedSections.size === 0 && sortedSections.length > 0) {
        setExpandedSections(new Set([sortedSections[0].id]));
      }
    } catch (error: any) {
      console.error("Bölümler yüklenirken hata:", error);
      await showAlert({
        title: "Hata",
        message: "Bölümler yüklenirken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (surveyId) {
      loadSections();
    }
  }, [surveyId]);

  const resetSectionForm = () => {
    setSectionForm({
      title: "",
      description: "",
    });
  };

  const handleAddSection = async () => {
    if (!canUpdateSurvey) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    setEditingSection("new");
    resetSectionForm();
  };

  const handleSectionSave = async () => {
    if (!sectionForm.title.trim()) {
      await showAlert({
        title: "Uyarı",
        message: "Bölüm başlığı zorunludur.",
        type: "warning",
      });
      return;
    }

    try {
      setSavingSection(editingSection || "new");

      const payload = {
        title: sectionForm.title.trim(),
        description: sectionForm.description.trim(),
        order: editingSection
          ? sections.find((s) => s.id === editingSection)?.order ?? sections.length
          : sections.length,
      };

      if (editingSection && editingSection !== "new") {
        await ApiService.updateSurveySection(surveyId, editingSection, payload);
      } else {
        const newSection = await ApiService.createSurveySection(surveyId, payload);
        if (newSection?.id || newSection?.data?.id) {
          const sectionId = newSection.id || newSection.data.id;
          setExpandedSections((prev) => new Set([...prev, sectionId]));
        }
      }

      setEditingSection(null);
      resetSectionForm();
      await loadSections();
    } catch (error: any) {
      console.error("Bölüm kaydetme hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Bölüm kaydedilirken hata oluştu.",
        type: "error",
      });
    } finally {
      setSavingSection(null);
    }
  };

  const handleEditSection = (section: Section) => {
    if (!canUpdateSurvey) {
      showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    setEditingSection(section.id);
    setSectionForm({
      title: section.title,
      description: section.description || "",
    });
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!canUpdateSurvey) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    const section = sections.find((s) => s.id === sectionId);
    const confirmed = await showConfirm({
      title: "Bölümü Sil",
      message: `"${section?.title}" bölümünü silmek istediğinizden emin misiniz? Bu bölümdeki tüm sorular da silinecektir.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      try {
        setDeletingSection(sectionId);
        await ApiService.deleteSurveySection(surveyId, sectionId);

        setExpandedSections((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sectionId);
          return newSet;
        });

        await loadSections();
      } catch (error: any) {
        console.error("Bölüm silme hatası:", error);
        await showAlert({
          title: "Hata",
          message: error.message || "Bölüm silinirken hata oluştu.",
          type: "error",
        });
      } finally {
        setDeletingSection(null);
      }
    }
  };

  const toggleSectionExpansion = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const reorderSections = async (fromIndex: number, toIndex: number) => {
    if (!canUpdateSurvey) return;

    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);

    const updatedSections = newSections.map((section, index) => ({
      ...section,
      order: index,
    }));

    setSections(updatedSections);
    onSectionsChange?.(updatedSections);

    try {
      await Promise.all(
        updatedSections.map((section, index) =>
          ApiService.updateSurveySection(surveyId, section.id, {
            order: index,
          })
        )
      );
    } catch (error) {
      console.error("Bölüm sırası güncellenirken hata:", error);
      await showAlert({
        title: "Hata",
        message: "Bölüm sırası güncellenirken hata oluştu.",
        type: "error",
      });
      await loadSections();
    }
  };

  const handleDragStart = (index: number) => {
    if (!canUpdateSurvey) return;
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (index: number) => {
    if (dragIndex !== null && dragIndex !== index) {
      await reorderSections(dragIndex, index);
    }
    setDragIndex(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {canUpdateSurvey && !editingSection && (
        <button
          onClick={handleAddSection}
          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600">
          <Plus className="w-5 h-5" />
          Yeni Bölüm Ekle
        </button>
      )}

      {editingSection === "new" && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Yeni Bölüm</h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bölüm Başlığı *</label>
              <input
                type="text"
                name="section_title"
                id="section_title"
                autoComplete="off"
                value={sectionForm.title}
                onChange={(e) => setSectionForm((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: Genel Bilgiler"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
              <textarea
                name="section_description"
                id="section_description"
                autoComplete="off"
                value={sectionForm.description}
                onChange={(e) =>
                  setSectionForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: Bu bölümde sizinle ilgili temel bilgileri öğrenmek istiyoruz."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setEditingSection(null);
                  resetSectionForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                İptal
              </button>
              <button
                type="button"
                onClick={handleSectionSave}
                disabled={savingSection !== null}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {savingSection !== null && (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                )}
                {savingSection !== null ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {sections.length === 0 && !editingSection ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-lg font-medium">Henüz Bölüm Eklenmemiş</p>
          <p className="text-sm">Anketinizi organize etmek için bölümler ekleyin.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section, index) => {
            const isExpanded = expandedSections.has(section.id);
            const isEditing = editingSection === section.id;

            return (
              <div
                key={section.id}
                onDragOver={handleDragOver}
                onDrop={() => {
                  void handleDrop(index);
                }}
                className={`bg-white border rounded-lg shadow-sm ${isEditing ? 'border-blue-200 ring-1 ring-blue-100' : 'border-gray-200'} ${
                  canUpdateSurvey && !isEditing ? "cursor-move" : ""
                }`}>
                {isEditing ? (
                  <div className="p-6 bg-blue-50/50 rounded-t-lg border-b border-blue-100">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">{index + 1}. Bölümü Düzenle</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bölüm Başlığı *</label>
                        <input
                          type="text"
                          name="section_title_edit"
                          id={`section_title_${section.id}`}
                          autoComplete="off"
                          value={sectionForm.title}
                          onChange={(e) => setSectionForm((prev) => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="Örn: Genel Bilgiler"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
                        <textarea
                          name="section_description_edit"
                          id={`section_description_${section.id}`}
                          autoComplete="off"
                          value={sectionForm.description}
                          onChange={(e) =>
                            setSectionForm((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="Örn: Bu bölümde sizinle ilgili temel bilgileri öğrenmek istiyoruz."
                        />
                      </div>

                      <div className="flex justify-end space-x-3 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSection(null);
                            resetSectionForm();
                          }}
                          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                          İptal
                        </button>
                        <button
                          type="button"
                          onClick={handleSectionSave}
                          disabled={savingSection !== null}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                          {savingSection !== null && (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          )}
                          {savingSection !== null ? "Kaydediliyor..." : "Kaydet"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {canUpdateSurvey && (
                          <div
                            className="p-1 text-gray-400 cursor-move"
                            title="Sürükleyerek sırayı değiştir"
                            draggable={!isEditing}
                            onDragStart={() => handleDragStart(index)}
                          >
                            <GripVertical className="w-4 h-4" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleSectionExpansion(section.id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                          )}
                        </button>

                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 break-all">
                            {index + 1}. {section.title}
                          </h4>
                          {section.description && <p className="text-sm text-gray-600 mt-1 break-all">{section.description}</p>}
                        </div>
                      </div>

                      {canUpdateSurvey && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditSection(section)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Bölümü Düzenle">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSection(section.id)}
                            disabled={deletingSection === section.id}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Bölümü Sil">
                            {deletingSection === section.id ? (
                              <div className="animate-spin h-4 w-4 border-2 rounded-full border-t-transparent border-red-600"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isExpanded && (
                  <div className="p-4">
                    <QuestionManager surveyId={surveyId} sectionId={section.id} canUpdateSurvey={canUpdateSurvey} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
